import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import PaginatedTable from "Components/shared/PaginatedTable";
import { getDriversRequests } from "services/driver";
import DriverRequestsSearch from "./DriversRequestsSearch";
import formatToUTC from "utils/formatToUTC";
import { driverRequestStatus } from "enums/shared";

import { useNavigate } from "react-router-dom";
import { useRTL } from "hooks/isRTL";

import { Typography } from "@mui/material";
import { debounce } from "utils/debounce";
import {
  driverPageAtom,
  driverRequestsFiltersAtom,
  driverRowsPerPageAtom,
} from "./atoms/driverRequestsFiltersAtom";
import { useAtom } from "jotai";

const DriversRequestsTable = () => {
  const [filters, setFilters] = useAtom(driverRequestsFiltersAtom);
  const [page, setPage] = useAtom(driverPageAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(driverRowsPerPageAtom);

  const { t, isRTL } = useRTL();

  const navigate = useNavigate();

  const columns = [
    {
      id: "orderNumber",
      label: t("orderNumber"),
      renderCell: (row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "text.lightBlue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/orders/${row.orderId}`);
          }}
        >
          {row.orderNumber}
        </Typography>
      ),
    },
    {
      id: "creationDate",
      label: t("creationDate"),
      sortable: true,
    },
    {
      id: "status",
      label: t("status"),
      sortable: true,
      name: "driverRequestStatus",
    },
  ];

  const customColumnStyles = {
    orderNumber: { flex: "1", textAlign: isRTL ? "right" : "left" },
    creationDate: { flex: "1", textAlign: isRTL ? "right" : "left" },
    status: { flex: "1", textAlign: isRTL ? "right" : "left" },
  };

  const fetchDriversRequests = async () => {
    const { searchBar, status, descendingCreationDateSort } = filters;

    return getDriversRequests(
      rowsPerPage,
      page + 1,
      searchBar || "",
      status || null,
      descendingCreationDateSort
    );
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "driversRequests",
      { page: page + 1, pageSize: rowsPerPage, ...filters },
    ],
    queryFn: fetchDriversRequests,
    select: (data) => {
      const rows = data.page.map((request) => ({
        ...request,
        creationDate: request.creationDate.split("T")[0],
        status: t(driverRequestStatus[request.status] || "Unknown"),
      }));

      return {
        rows,
        totalRecords: data.totalRecords,
      };
    },
    keepPreviousData: true,
  });

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const debouncedUpdateFilters = useMemo(
    () =>
      debounce((newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
        setPage(0);
      }, 500),
    []
  );

  const updateFilters = useCallback(
    (newFilters) => {
      debouncedUpdateFilters(newFilters);
    },
    [debouncedUpdateFilters]
  );

  const handleRowClick = (row) => {
    navigate(`/drivers-requests/${row.id}?page=${page}`);
  };

  const handleSortChange = (columnId, isDescending) => {
    if (columnId === "creationDate") {
      setFilters((prev) => ({
        ...prev,
        descendingCreationDateSort: isDescending,
      }));
      setPage(0);
    }
  };

  return (
    <>
      <DriverRequestsSearch />
      <PaginatedTable
        columns={columns}
        rows={data?.rows || []}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={data?.totalRecords || 0}
        customColumnStyles={customColumnStyles}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        showActions={false}
        onRowClick={handleRowClick}
        filters={filters} // Pass the filters object
        onSortChange={handleSortChange} // Pass the new sort handler
        statusOptions={Object.entries(driverRequestStatus).map(
          ([key, value]) => ({
            value: key,
            label: t(value),
          })
        )}
        onRegistrationStatusChange={(newStatuses) => {
          setFilters((prev) => ({ ...prev, status: newStatuses }));
          setPage(0);
        }}
      />
    </>
  );
};

export default DriversRequestsTable;
